import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Skeleton,
  Paper,
  Tooltip,
  CardActionArea,
  alpha
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import type { Category, Subcategory, Article } from '../types/category';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SortIcon from '@mui/icons-material/Sort';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useFavorites } from '../hooks/useFavorites';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useThemeMode } from '../App';
import { motion, AnimatePresence } from 'framer-motion';

const CategoryPage: React.FC = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string; subcategoryId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const favorites = useFavorites();
  
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'az' | 'za'>('newest');
  const [tabLoading, setTabLoading] = useState(false);

  // Функция для очистки текста от Markdown и получения первых строк
  const getCleanPreview = (content: string, maxLength: number = 150): string => {
    // Удаляем заголовки
    let cleanText = content.replace(/^#+\s/gm, '');
    
    // Удаляем жирный и курсивный текст
    cleanText = cleanText.replace(/\*\*|\*|__|_/g, '');
    
    // Удаляем код
    cleanText = cleanText.replace(/`{1,3}[^`]*`{1,3}/g, '');
    
    // Удаляем ссылки, оставляя только текст
    cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Удаляем списки
    cleanText = cleanText.replace(/^\s*[-*+]\s/gm, '');
    cleanText = cleanText.replace(/^\s*\d+\.\s/gm, '');
    
    // Удаляем пустые строки и лишние пробелы
    cleanText = cleanText.replace(/\n\s*\n/g, '\n').trim();
    
    // Получаем первые строки
    const lines = cleanText.split('\n').slice(0, 3);
    
    // Объединяем строки и обрезаем до maxLength
    let preview = lines.join(' ').trim();
    if (preview.length > maxLength) {
      preview = preview.substring(0, maxLength) + '...';
    }
    
    return preview;
  };

  const fetchArticles = async (categoryId: string, subcategoryId?: string) => {
    try {
      const url = `https://guide.zerdegroup.kz/api/articles?category_id=${categoryId}${
        subcategoryId && subcategoryId !== 'all' ? `&subcategory_id=${subcategoryId}` : ''
      }&isPublished=true`;
      console.log('Fetching articles from:', url);
      
      setTabLoading(true);
      
      const articlesResponse = await fetch(url);
      if (!articlesResponse.ok) throw new Error('Ошибка при загрузке статей');
      const articlesData = await articlesResponse.json();
      console.log('Articles response:', articlesData);
      setArticles(articlesData.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке статей');
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Загружаем категорию
        const categoryResponse = await fetch(`https://guide.zerdegroup.kz/api/categories/${categoryId}`);
        if (!categoryResponse.ok) throw new Error('Ошибка при загрузке категории');
        const categoryData = await categoryResponse.json();
        setCategory(categoryData);

        // Загружаем подкатегории
        const subcategoriesResponse = await fetch(`https://guide.zerdegroup.kz/api/subcategories/category/${categoryId}`);
        if (!subcategoriesResponse.ok) throw new Error('Ошибка при загрузке подкатегорий');
        const subcategoriesData = await subcategoriesResponse.json();
        setSubcategories(subcategoriesData);

        // Загружаем все статьи категории (без фильтрации)
        const allArticlesUrl = `https://guide.zerdegroup.kz/api/articles?category_id=${categoryId}&isPublished=true`;
        const allArticlesResponse = await fetch(allArticlesUrl);
        if (!allArticlesResponse.ok) throw new Error('Ошибка при загрузке статей');
        const allArticlesData = await allArticlesResponse.json();
        setAllArticles(allArticlesData.items || []);
        
        // Устанавливаем начальные статьи в зависимости от вкладки
        if (subcategoryId && subcategoryId !== 'all') {
          // Если есть subcategoryId, фильтруем из всех статей
          setArticles(allArticlesData.items.filter((article: Article) => 
            article.subcategory_id.toString() === subcategoryId
          ) || []);
        } else {
          // Иначе показываем все статьи
          setArticles(allArticlesData.items || []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  useEffect(() => {
    // Устанавливаем выбранную вкладку при изменении URL
    setSelectedTab(subcategoryId || 'all');
    
    // Фильтруем статьи локально при изменении subcategoryId
    if (!loading && allArticles.length > 0) {
      if (subcategoryId && subcategoryId !== 'all') {
        // Фильтруем статьи по подкатегории
        setArticles(allArticles.filter(article => 
          article.subcategory_id.toString() === subcategoryId
        ));
      } else {
        // Показываем все статьи
        setArticles(allArticles);
      }
    }
  }, [subcategoryId, allArticles, loading]);

  const handleTabChange = async (event: React.SyntheticEvent, newValue: string) => {
    // Сохраняем текущую позицию скролла
    const scrollPosition = window.scrollY;
    
    setSelectedTab(newValue);
    if (categoryId) {
      if (newValue === 'all') {
        navigate(`/category/${categoryId}`);
      } else {
        navigate(`/category/${categoryId}/${newValue}`);
      }
      
      // Фильтруем данные локально вместо повторной загрузки
      if (newValue === 'all') {
        setArticles(allArticles);
      } else {
        setArticles(allArticles.filter(article => 
          article.subcategory_id.toString() === newValue
        ));
      }
      
      // Восстанавливаем позицию скролла
      setTimeout(() => {
        window.scrollTo({
          top: scrollPosition,
          behavior: 'auto'
        });
      }, 0);
    }
  };

  // Сортировка статей
  const getSortedArticles = () => {
    if (!articles.length) return [];
    
    return [...articles].sort((a, b) => {
      switch (sortOrder) {
        case 'newest':
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        case 'oldest':
          return new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime();
        case 'az':
          return a.title.localeCompare(b.title);
        case 'za':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });
  };

  // Обработчик сортировки
  const handleSortChange = () => {
    const orders: Array<'newest' | 'oldest' | 'az' | 'za'> = ['newest', 'oldest', 'az', 'za'];
    const currentIndex = orders.indexOf(sortOrder);
    const nextIndex = (currentIndex + 1) % orders.length;
    setSortOrder(orders[nextIndex]);
  };

  // Получение текста для сортировки
  const getSortLabel = () => {
    switch (sortOrder) {
      case 'newest': return 'Сначала новые';
      case 'oldest': return 'Сначала старые';
      case 'az': return 'По алфавиту (А-Я)';
      case 'za': return 'По алфавиту (Я-А)';
      default: return '';
    }
  };

  // Форматирование даты
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'd MMMM yyyy', { locale: ru });
    } catch (error) {
      return '';
    }
  };

  // Функция для рендеринга статей с анимациями
  const renderArticles = (articlesToRender: Article[]) => {
    if (articlesToRender.length === 0) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 5,
              backgroundColor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              textAlign: 'center'
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              В этой категории пока нет статей
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Попробуйте выбрать другую категорию или вернитесь позже
            </Typography>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
            >
              На главную
            </Button>
          </Box>
        </motion.div>
      );
    }

    return (
      <Grid container spacing={3} sx={{overflowX: 'hidden', }}>
        <AnimatePresence mode="sync">
          {articlesToRender.map((article) => {
            const isFavorite = favorites.isFavorite(article.id.toString());
            const subcategoryTitle = subcategories.find(s => s.id === article.subcategory_id)?.title || '';
            // Определяем, новая ли статья (3 дня)
            const isNew = (Date.now() - new Date(article.created_at || '').getTime()) < 1000 * 60 * 60 * 24 * 3;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <motion.div
                  layout
                  layoutId={`article-${article.id}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{
                    layout: { type: "spring", stiffness: 350, damping: 25 },
                    opacity: { duration: 0.3 },
                    scale: { duration: 0.3 }
                  }}
                >
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                      background: mode === 'light'
                        ? alpha(theme.palette.background.paper, 0.98)
                        : alpha(theme.palette.background.paper, 0.90),
                      boxShadow: 'none',
                      transition: 'all 0.28s cubic-bezier(.23,1.02,.64,.98)',
                      position: 'relative',
                      p: 0,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background: theme.palette.primary.main,
                        opacity: 0.7,
                        borderTopLeftRadius: 6,
                        borderBottomLeftRadius: 6,
                        zIndex: 1,
                      },
                      '&:hover': {
                        transform: 'translateY(-7px) scale(1.025)',
                        boxShadow: mode === 'light' 
                          ? '0 8px 32px rgba(0,0,0,0.13)' 
                          : '0 8px 32px rgba(0,0,0,0.33)',
                        borderColor: theme.palette.primary.main,
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <CardActionArea 
                      onClick={() => navigate(`/article/${categoryId}/${article.subcategory_id}/${article.id}`)}
                      sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0, position: 'relative', zIndex: 2 }}
                    >
                      <CardContent sx={{ flex: 1, width: '100%', p: 1.7, pb: 1.1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 0.7, mb: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
                            <Chip 
                              size="small" 
                              label={category?.title}
                              sx={{ 
                                backgroundColor: alpha(theme.palette.primary.main, 0.10),
                                color: 'primary.main',
                                fontWeight: 500,
                                fontSize: '0.88rem',
                                letterSpacing: 0.1,
                                px: 0.8,
                                height: 22,
                                borderRadius: 1.5,
                              }}
                            />
                            <Chip 
                              size="small" 
                              label={subcategoryTitle}
                              sx={{ 
                                backgroundColor: alpha(theme.palette.secondary.main, 0.10),
                                color: 'secondary.main',
                                fontWeight: 500,
                                fontSize: '0.88rem',
                                letterSpacing: 0.1,
                                px: 0.8,
                                height: 22,
                                borderRadius: 1.5,
                              }}
                            />
                            {isNew && (
                              <Chip
                                size="small"
                                label=""
                                icon={<Box sx={{ width: 7, height: 7, bgcolor: theme.palette.success.main, borderRadius: '50%' }} />}
                                sx={{
                                  background: 'transparent',
                                  color: 'success.main',
                                  fontWeight: 700,
                                  fontSize: '0.88rem',
                                  px: 0.1,
                                  borderRadius: 1.5,
                                  ml: 0.3,
                                  minWidth: 0,
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                          <Tooltip title={isFavorite ? "Удалить из избранного" : "Добавить в избранное"}>
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (isFavorite) {
                                  favorites.removeFromFavorites(article.id.toString());
                                } else {
                                  favorites.addToFavorites({
                                    id: article.id.toString(),
                                    categoryId: categoryId!,
                                    subcategoryId: article.subcategory_id.toString(),
                                    title: article.title
                                  });
                                }
                              }}
                              sx={{ 
                                color: isFavorite ? 'error.main' : 'text.secondary',
                                p: 0.5,
                                '&:hover': {
                                  color: 'error.main',
                                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                                }
                              }}
                            >
                              {isFavorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography 
                          gutterBottom 
                          variant="h6" 
                          component="h3"
                          sx={{ 
                            fontWeight: 700,
                            lineHeight: 1.18,
                            mb: 1,
                            minHeight: '2.7rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            letterSpacing: 0.01,
                            px: 0,
                            background: 'none',
                            borderRadius: 0,
                            fontSize: '1.13rem',
                          }}
                        >
                          {article.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '2.5rem',
                            fontSize: '0.99rem',
                            letterSpacing: 0.01,
                            lineHeight: 1.5,
                          }}
                        >
                          {getCleanPreview(article.content, 180)}
                        </Typography>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 0.7,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
                            mt: 0.7,
                            marginTop: 'auto',
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              color: 'text.secondary',
                              gap: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            <AccessTimeIcon fontSize="inherit" />
                            {formatDate(article.created_at)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </AnimatePresence>
      </Grid>
    );
  };

  // Рендер скелетона при загрузке
  const renderSkeletons = () => (
    <Grid container spacing={3}>
      {[1, 2, 3, 4, 5, 6].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={<Skeleton height={30} width="80%" />}
              subheader={<Skeleton height={20} width="40%" />}
              action={<Skeleton variant="circular" width={32} height={32} />}
            />
            <Divider />
            <CardContent>
              <Skeleton height={20} />
              <Skeleton height={20} />
              <Skeleton height={20} width="80%" />
            </CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <Skeleton width={80} height={30} />
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton width="30%" height={40} sx={{ mb: 2 }} />
          <Skeleton width="60%" height={56} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton width="100%" height={48} />
        </Box>
        {renderSkeletons()}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!category) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Категория не найдена</Alert>
      </Box>
    );
  }

  const selectedSubcategory = subcategories.find(sub => subcategoryId && sub.id === Number(subcategoryId));
  const sortedArticles = getSortedArticles();

  return (
    <Box sx={{ minHeight: '70vh' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          background: theme => `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.primary.dark}10)`,
          borderBottom: theme => `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            sx={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={() => navigate('/')}
          >
            <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
            Главная
          </Link>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center' 
            }}
          >
            <FolderOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
            {category.title}
          </Typography>
          {selectedSubcategory && (
            <Typography color="text.primary">{selectedSubcategory.title}</Typography>
          )}
        </Breadcrumbs>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: 2
        }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom={isMobile}
            sx={{ 
              fontWeight: 'bold',
              color: theme => theme.palette.primary.dark
            }}
          >
            {category.title}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <Chip
              icon={<DescriptionOutlinedIcon />}
              label={selectedSubcategory 
                ? `${selectedSubcategory.articles_count} статей`
                : `${articles.length} статей`
              }
              color="primary"
              variant="outlined"
              size="small"
            />
            <Tooltip title="Изменить порядок сортировки">
              <Button
                size="small"
                onClick={handleSortChange}
                startIcon={<SortIcon />}
                sx={{ fontSize: '0.75rem' }}
              >
                {getSortLabel()}
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', position: 'sticky', top: 0, zIndex: 10 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="category tabs"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : undefined}
          allowScrollButtonsMobile
          sx={{ 
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              height: 3,
              transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            },
            '& .Mui-selected': {
              fontWeight: 'bold',
              color: 'primary.main',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            },
            '& .MuiTab-root': {
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }
          }}
        >
          <Tab 
            label={`Все статьи (${allArticles.length})`}
            value="all"
            sx={{ 
              textTransform: 'none',
              fontSize: '0.9rem',
              minHeight: 48,
            }}
          />
          {subcategories.map((sub) => (
            <Tab 
              key={sub.id}
              label={`${sub.title} (${sub.articles_count})`}
              value={sub.id.toString()}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.9rem',
                minHeight: 48,
              }}
            />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ 
        transition: 'opacity 200ms ease-in-out',
        opacity: tabLoading ? 0.7 : 1,
        minHeight: articles.length > 0 ? 'auto' : '300px',
        position: 'relative',
      }}>
        {renderArticles(sortedArticles)}
      </Box>
    </Box>
  );
};

export default CategoryPage; 